import React from "react";
import { Helmet } from "react-helmet-async";
import clientConfig from "../clientConfig";

const DynamicHead = ({ client }) => {
  const normalizedClient = (client || "").toLowerCase();
  const config = clientConfig[normalizedClient] || clientConfig.default;
  const isDelta = normalizedClient === "delta";

  // Handle backward compatibility - check if fonts is array (old format) or object (new format)
  const renderFonts = () => {
    if (!config.fonts) return null;

    // Google Fonts
    if (config.fonts.type === "google") {
      const fontFamilies = config.fonts.urls
        .map((url) => {
          const match = url.match(/family=([^:&]+)/);
          if (match) {
            return match[1].replace(/\+/g, " ");
          }
          return null;
        })
        .filter(Boolean);

      const fontCSS =
        fontFamilies.length > 0
          ? `
        body, .App, .App * {
          font-family: "${fontFamilies.join(
            '", "'
          )}", "Montserrat", "Verdana", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
        }
      `
          : "";

      return (
        <>
          {config.fonts.urls.map((url, index) => (
            <link key={`google-font-${index}`} href={url} rel="stylesheet" />
          ))}
          {fontCSS && <style type="text/css">{fontCSS}</style>}
        </>
      );
    }

    // Local Fonts
    if (config.fonts.type === "local") {
      const getFontFormat = (fileName) => {
        const normalized = String(fileName || "").toLowerCase();
        if (normalized.endsWith(".woff2")) return "woff2";
        if (normalized.endsWith(".woff")) return "woff";
        if (normalized.endsWith(".ttf")) return "truetype";
        if (normalized.endsWith(".otf")) return "opentype";
        if (normalized.endsWith(".eot")) return "embedded-opentype";
        return "opentype";
      };

      const fontFaceCSS = config.fonts.files
        .map(
          (f) => `
            @font-face {
              font-family: "${config.fonts.fontFamily}";
              src: url("/fonts/${f.file}") format("${getFontFormat(f.file)}");
              font-weight: ${f.weight};
              font-style: ${f.style};
              font-display: swap;
            }`
        )
        .join("\n");

      const bodyCSS = isDelta
        ? `
        body, .App, .App * {
          font-family: "Verdana",
            -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
        }
        h1, h2, h3, h4, h5, h6, button, .Button {
          font-family: "${config.fonts.fontFamily}", "Verdana",
            -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
        }
        `
        : `
        body, .App, .App * {
          font-family: "${config.fonts.fontFamily}", "Verdana",
            -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif !important;
        }
        `;

      const combinedCSS = `${fontFaceCSS}\n${bodyCSS}`;

      return <style type="text/css">{combinedCSS}</style>;
    }

    return null;
  };

  return (
    <Helmet>
      <title>{config.title}</title>
      <meta name="description" content={config.description} />
      <meta name="theme-color" content={config.themeColor} />
      <link rel="icon" href={config.favicon} />
      <link rel="apple-touch-icon" href={config.favicon} />
      {renderFonts()}
    </Helmet>
  );
};

export default DynamicHead;
